import { PalletConfigObj } from 'blackbox';
import {
	StackQueueItem,
	IOLEVEL,
	PalletizerStatus,
	StackQueueStatus,
	Application,
} from 'libapi';
import { useState, useContext, useEffect } from 'react';
import {
	PalletizerContext,
	RuntimeContext,
} from '../../context/PalletizerContext';
import IOStatus from '../common/IOUtils';
import ProgressRing from '../common/ProgressRing';
import {
	getBarcodeStartModeEnabled,
	useCoreAppConfig,
	useMachineConfig,
} from '../General';
import Button from '../PendantButton';
import { COLOR } from '../shared/shared';
import { partitionObject, Position } from './OperationPage';
import { ApplicationContext } from '../../App';

interface PalletPaneProps {
	pTitle: string;
	pSelectedStackItem: StackQueueItem | undefined;
	pPalletConfig: PalletConfigObj;
	pIODict: { [key: string]: IOLEVEL | IOLEVEL[] };
	pInvokeEditBay: () => void;
	pBayID: number;
	positions: Position[];
	positionIndex: number;
	palletFull?: boolean;
	pBPM?: string;
}

export function PalletPane({
	pTitle,
	pSelectedStackItem,
	pPalletConfig,
	pIODict,
	pInvokeEditBay,
	pBayID,
	positions,
	positionIndex,
	palletFull = false,
	pBPM,
}: PalletPaneProps) {
	const lBarCodeScannerStartMode = getBarcodeStartModeEnabled();
	const app = useContext(ApplicationContext) as Application;
	const [lSlipSheetsPresent, setSlipSheetPresent] = useState<boolean>(false);
	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;
	const status = lRuntimeContext.operational_state.status;
	const add_or_edit_disabled =
		status === PalletizerStatus.Running || status === PalletizerStatus.Homing;
	const [lBoxesInStack, lSetBoxesInStack] = useState<number>(0);
	const [lSSInStack, lSetSSInStack] = useState<number>(0);
	const [boxCount, setBoxCount] = useState<number>(0);
	const [slipsheetCount, setSlipSheetCount] = useState<number>(0);

	const lMachineConfig = useMachineConfig(app).lMachineConfig;

	useEffect(() => {
		let lHas = false;
		lMachineConfig?.Bays.forEach(bay => {
			bay.SlipSheetMags.forEach(m => {
				lHas = m || lHas;
			});
		});
		setSlipSheetPresent(lHas);
	}, [lMachineConfig]);

	useEffect(() => {
		if (!pSelectedStackItem || !pPalletConfig) return;
		const lBoxCount = pPalletConfig.getBoxCountStack(
			pSelectedStackItem.PalletID,
			pSelectedStackItem.StackID
		);

		lSetBoxesInStack(lBoxCount);
	}, [pSelectedStackItem]);

	useEffect(() => {
		let boxes = 0;
		let slipsheets = 0;

		for (let i = 0; i <= positionIndex - 1 && i < positions.length; i++) {
			const item = positions[i];
			if (item.type === 'Box') boxes++;
			else if (item.type === 'Slipsheet') slipsheets++;
		}
		setBoxCount(boxes);
		setSlipSheetCount(slipsheets);
	}, [positionIndex]);

	useEffect(() => {
		if (!pSelectedStackItem || !pPalletConfig) return;
		const lSSCount = pPalletConfig.getSSCountStack(
			pSelectedStackItem.PalletID,
			pSelectedStackItem.StackID
		);
		lSetSSInStack(lSSCount);
	}, [pSelectedStackItem?.SlipSheetsPlaced]);

	// TODO Clean up local2global callss
	function getBoxProgress(
		pSelectedStackItem: StackQueueItem | undefined
	): number {
		if (!pSelectedStackItem || !pPalletConfig || lBoxesInStack === 0) return 0;
		if (palletFull) return 100;
		else return (boxCount / lBoxesInStack) * 100;
	}
	function getBoxProgressString(
		pSelectedStackItem: StackQueueItem | undefined
	): string {
		if (!pSelectedStackItem || !pPalletConfig || lBoxesInStack === 0) return '-';
		if (palletFull) return `${lBoxesInStack} / ${lBoxesInStack}`;
		else return `${boxCount} / ${lBoxesInStack}`;
	}

	function getSSProgressString(
		pSelectedStackItem: StackQueueItem | undefined
	): string {
		if (!pSelectedStackItem || !pPalletConfig || lSSInStack === 0) return '-';
		if (palletFull) return `${lSSInStack}/${lSSInStack}`;
		return `${slipsheetCount}/${lSSInStack}`;
	}

	function getSSProgress(
		pSelectedStackItem: StackQueueItem | undefined
	): number {
		if (!pSelectedStackItem || !pPalletConfig || lSSInStack === 0) return 0;
		if (palletFull) return 100;
		return (slipsheetCount / lSSInStack) * 100;
	}

	function getIOColorsFromLevels(
		pIOLevels: IOLEVEL[] | IOLEVEL
	): [COLOR[], COLOR[]] {
		const lColors: COLOR[] = [];
		const lShadowColors: COLOR[] = [];
		if (!Array.isArray(pIOLevels)) {
			pIOLevels = [pIOLevels];
		}
		for (const level of pIOLevels) {
			let lColor = COLOR.BORDER_GREY;
			let lShadowColor = COLOR.TRANSPARENT;
			switch (level) {
				case IOLEVEL.GOOD:
					lColor = COLOR.GREEN;
					lShadowColor = COLOR.TRANSPARENT_GREEN;
					break;
				case IOLEVEL.BAD:
					lColor = COLOR.RED_CLEAR;
					lShadowColor = COLOR.RED_CLEAR;
					break;
				case IOLEVEL.WARN:
					lColor = COLOR.RED_CLEAR;
					lShadowColor = COLOR.RED_CLEAR;
					break;
				default:
					break;
			}

			lColors.push(lColor);
			lShadowColors.push(lShadowColor);
		}

		return [lColors, lShadowColors];
	}

	return (
		<div className={'full-height width-50 flex-center align-center'}>
			<div className="full-width-minus-2pad full-height-minus-2pad flex-col rounded">
				{/* Info section */}
				<div className="height-8 full-width background-silver rounded-top flex-row flex-between align-center">
					<div className="flex-row full-height align-center">
						<div className="">
							<span className="font-5 bold-font color-blue double-margin-left">
								{pTitle}
							</span>
						</div>

						<span className="font-5 color-blue double-margin-left">
							{pSelectedStackItem && pPalletConfig
								? pPalletConfig.getStack(
										pSelectedStackItem?.PalletID,
										pSelectedStackItem.StackID
								  ).name
								: 'No stack selected'}
						</span>
					</div>

					{!lBarCodeScannerStartMode && (
						<Button
							className="height-80 margin-right"
							pAction={pInvokeEditBay}
							pDisabled={add_or_edit_disabled}
							pNormalColorOverride={COLOR.DARK_BLUE}
						>
							<span className="double-padding-left double-padding-right bold-font">
								Edit
							</span>
						</Button>
					)}
				</div>

				<section
					className="full-width-minus-pad flex-col height-40 double-margin-top"
					style={{ zIndex: '4', alignItems: `${pBayID === 0 ? 'start' : 'end'}` }}
				>
					{/* text and progress bars */}
					<div className="flex-column">
						{/* ios */}
						<div className="flex-row padding-top">
							{partitionObject({ ...pIODict }, 2).map((pPartition, pIndex) => {
								return (
									<div key={pIndex} className="flex-col margin-right">
										{Object.keys(pPartition).map(function (pKey: string, pIndex) {
											const lIOLevels: IOLEVEL = pPartition[pKey];
											const lColors = getIOColorsFromLevels(lIOLevels);
											return (
												<div key={pIndex} className="padding-top">
													<IOStatus
														pColor={lColors[0]}
														pShadowColor={lColors[1]}
														pTitle={pKey}
														pSize={30}
													/>
												</div>
											);
										})}
									</div>
								);
							})}
						</div>
						<div
							className={`flex-row align-center`}
							style={{ justifyContent: pBayID === 0 ? 'start' : 'end' }}
						>
							<ProgressRing
								pSize={170}
								pStrokeWidth={9}
								pProgress={getBoxProgress(pSelectedStackItem)}
							>
								<div className="flex-col full-size flex-center align-center half-padding-top">
									<span className="font-1">BOXES</span>
									<span className={`font-5 bold-font`}>
										{getBoxProgressString(pSelectedStackItem)}
									</span>
									<span className="font-1">{pBPM ? `${pBPM} BPM` : ' '}</span>
								</div>
							</ProgressRing>
							{lSlipSheetsPresent && (
								<ProgressRing
									pSize={170}
									pStrokeWidth={8}
									pProgress={getSSProgress(pSelectedStackItem)}
								>
									<div className="flex-col full-size flex-center align-center half-padding-top">
										<span className="font-1">SLIP SHEETS</span>
										<span className="font-5 bold-font">
											{getSSProgressString(pSelectedStackItem)}
										</span>
									</div>
								</ProgressRing>
							)}
						</div>
					</div>
				</section>
			</div>
		</div>
	);
}
